'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import ServiceCard from './ServiceCard';
import FilterPanel from './FilterPanel';
import ComparisonModal from './ComparisonModal';

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
  image: string;
  imageAlt: string;
  category: string;
  complexity: string;
  roiTimeline: string;
  businessFunction: string;
  benefits: string[];
  implementationDuration: string;
  roiMetric: string;
  detailLink: string;
  features: string[];
  pricing: string;
}

const ServicesOverviewInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    category: 'all',
    complexity: 'all',
    roiTimeline: 'all',
    businessFunction: 'all'
  });
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockServices: Service[] = [
  {
    id: '1',
    title: 'AI Chatbot Implementatie',
    description: 'Intelligente chatbots die 24/7 klantvragen beantwoorden en leads kwalificeren met natuurlijke taalverwerking.',
    icon: 'ChatBubbleLeftRightIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15ea8f77e-1772247228739.png",
    imageAlt: 'Modern chatbot interface on laptop screen with blue and white conversation bubbles in minimalist office setting',
    category: 'Klantenservice',
    complexity: 'Gemiddeld',
    roiTimeline: '3-6 maanden',
    businessFunction: 'Klantenservice',
    benefits: [
    '80% minder klantenservice kosten',
    '24/7 beschikbaarheid zonder extra personeel',
    'Directe antwoorden op veelgestelde vragen',
    'Automatische lead kwalificatie'],

    implementationDuration: '4-6 weken',
    roiMetric: '€15.000 besparing per jaar',
    detailLink: '/ai-chatbot',
    features: [
    'Natuurlijke taalverwerking',
    'Multi-channel integratie',
    'Conversatie analytics',
    'CRM koppeling'],

    pricing: 'Vanaf €2.500/maand'
  },
  {
    id: '2',
    title: 'Procesautomatisering',
    description: 'Automatiseer repetitieve taken en workflows met AI-gedreven oplossingen voor maximale efficiëntie.',
    icon: 'Cog6ToothIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15de4852b-1772539704039.png",
    imageAlt: 'Digital workflow automation dashboard showing connected process nodes and data flow charts on dark background',
    category: 'Automatisering',
    complexity: 'Hoog',
    roiTimeline: '6-12 maanden',
    businessFunction: 'Operaties',
    benefits: [
    '70% tijdsbesparing op handmatige taken',
    'Minder fouten door menselijke tussenkomst',
    'Schaalbaarheid zonder extra personeel',
    'Real-time proces monitoring'],

    implementationDuration: '8-12 weken',
    roiMetric: '€25.000 besparing per jaar',
    detailLink: '/procesautomatisering',
    features: [
    'Workflow automatisering',
    'Document verwerking',
    'Data extractie',
    'Systeem integraties'],

    pricing: 'Vanaf €4.000/maand'
  },
  {
    id: '3',
    title: 'Predictive Analytics',
    description: 'Voorspel trends en klantgedrag met geavanceerde machine learning modellen voor betere besluitvorming.',
    icon: 'ChartBarIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_166783c0b-1772629263035.png",
    imageAlt: 'Business analytics dashboard with colorful graphs, charts and predictive trend lines on multiple monitors',
    category: 'Data & Analytics',
    complexity: 'Hoog',
    roiTimeline: '6-12 maanden',
    businessFunction: 'Marketing',
    benefits: [
    '40% betere voorspellingsnauwkeurigheid',
    'Data-gedreven besluitvorming',
    'Identificatie van nieuwe kansen',
    'Risico minimalisatie'],

    implementationDuration: '10-14 weken',
    roiMetric: '€30.000 extra omzet per jaar',
    detailLink: '/voorspellende-analytics',
    features: [
    'Voorspellende modellen',
    'Klant segmentatie',
    'Trend analyse',
    'Custom dashboards'],

    pricing: 'Vanaf €5.000/maand'
  },
  {
    id: '4',
    title: 'Content Generatie AI',
    description: 'Genereer hoogwaardige marketing content, productbeschrijvingen en social media posts met AI.',
    icon: 'DocumentTextIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19e6234db-1771885038923.png",
    imageAlt: 'Creative workspace with laptop displaying AI content generation interface and marketing materials spread on desk',
    category: 'Marketing',
    complexity: 'Laag',
    roiTimeline: '1-3 maanden',
    businessFunction: 'Marketing',
    benefits: [
    '10x snellere content productie',
    'Consistente merkboodschap',
    'SEO geoptimaliseerde teksten',
    'Multi-taal ondersteuning'],

    implementationDuration: '2-4 weken',
    roiMetric: '€8.000 besparing per jaar',
    detailLink: '/social-media-automatisering',
    features: [
    'Blog artikelen',
    'Product beschrijvingen',
    'Social media posts',
    'Email campagnes'],

    pricing: 'Vanaf €1.500/maand'
  },
  {
    id: '5',
    title: 'Computer Vision',
    description: 'Automatische beeldherkenning en analyse voor kwaliteitscontrole, inventarisatie en veiligheid.',
    icon: 'EyeIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e334d41a-1768656225140.png",
    imageAlt: 'Computer vision system analyzing product images on conveyor belt in modern manufacturing facility',
    category: 'Automatisering',
    complexity: 'Hoog',
    roiTimeline: '6-12 maanden',
    businessFunction: 'Operaties',
    benefits: [
    '99% nauwkeurigheid in detectie',
    'Real-time monitoring',
    'Minder kwaliteitsfouten',
    'Automatische rapportage'],

    implementationDuration: '12-16 weken',
    roiMetric: '€35.000 besparing per jaar',
    detailLink: '/document-intelligence',
    features: [
    'Object detectie',
    'Kwaliteitscontrole',
    'Gezichtsherkenning',
    'Defect analyse'],

    pricing: 'Vanaf €6.000/maand'
  },
  {
    id: '6',
    title: 'Voice AI Assistenten',
    description: 'Spraakgestuurde AI assistenten voor hands-free bedrijfsvoering en klantinteractie.',
    icon: 'MicrophoneIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_105d48fcc-1772152693927.png",
    imageAlt: 'Smart speaker device with glowing blue voice activation light on modern office desk',
    category: 'Klantenservice',
    complexity: 'Gemiddeld',
    roiTimeline: '3-6 maanden',
    businessFunction: 'Klantenservice',
    benefits: [
    'Hands-free operaties',
    'Natuurlijke spraakinteractie',
    'Multi-taal ondersteuning',
    'Integratie met bestaande systemen'],

    implementationDuration: '6-8 weken',
    roiMetric: '€12.000 besparing per jaar',
    detailLink: '/voice-ai',
    features: [
    'Spraakherkenning',
    'Commando verwerking',
    'Context begrip',
    'Voice analytics'],

    pricing: 'Vanaf €3.000/maand'
  },
  {
    id: '7',
    title: 'Sentiment Analyse',
    description: 'Analyseer klantfeedback en social media voor inzicht in merkperceptie en klanttevredenheid.',
    icon: 'HeartIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f84dfad-1767965143310.png",
    imageAlt: 'Social media sentiment analysis dashboard showing emoji reactions and customer feedback metrics',
    category: 'Data & Analytics',
    complexity: 'Gemiddeld',
    roiTimeline: '3-6 maanden',
    businessFunction: 'Marketing',
    benefits: [
    'Real-time sentiment tracking',
    'Crisis detectie',
    'Klant insights',
    'Concurrentie analyse'],

    implementationDuration: '4-6 weken',
    roiMetric: '€10.000 extra omzet per jaar',
    detailLink: '/sentiment-analyse',
    features: [
    'Social media monitoring',
    'Review analyse',
    'Trend detectie',
    'Rapportage dashboards'],

    pricing: 'Vanaf €2.000/maand'
  },
  {
    id: '8',
    title: 'Personalisatie Engine',
    description: 'Lever gepersonaliseerde ervaringen aan elke klant met AI-gedreven aanbevelingen en content.',
    icon: 'UserGroupIcon',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a6ef109-1769507986418.png",
    imageAlt: 'Personalized user interface showing customized product recommendations and tailored content on tablet screen',
    category: 'Marketing',
    complexity: 'Gemiddeld',
    roiTimeline: '3-6 maanden',
    businessFunction: 'Verkoop',
    benefits: [
    '35% hogere conversie',
    'Betere klantbinding',
    'Verhoogde order waarde',
    'Gepersonaliseerde journeys'],

    implementationDuration: '6-8 weken',
    roiMetric: '€20.000 extra omzet per jaar',
    detailLink: '/service-detail',
    features: [
    'Product aanbevelingen',
    'Dynamic content',
    'A/B testing',
    'Behavioral targeting'],

    pricing: 'Vanaf €3.500/maand'
  }];


  const handleFilterChange = (filterType: string, value: string) => {
    setActiveFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      category: 'all',
      complexity: 'all',
      roiTimeline: 'all',
      businessFunction: 'all'
    });
    setSearchQuery('');
  };

  const toggleComparison = (serviceId: string) => {
    setSelectedForComparison((prev) => {
      if (prev.includes(serviceId)) {
        return prev.filter((id) => id !== serviceId);
      } else if (prev.length < 3) {
        return [...prev, serviceId];
      }
      return prev;
    });
  };

  const removeFromComparison = (serviceId: string) => {
    setSelectedForComparison((prev) => prev.filter((id) => id !== serviceId));
  };

  const filteredServices = mockServices.filter((service) => {
    const matchesCategory = activeFilters.category === 'all' || service.category === activeFilters.category;
    const matchesComplexity = activeFilters.complexity === 'all' || service.complexity === activeFilters.complexity;
    const matchesRoiTimeline = activeFilters.roiTimeline === 'all' || service.roiTimeline === activeFilters.roiTimeline;
    const matchesBusinessFunction = activeFilters.businessFunction === 'all' || service.businessFunction === activeFilters.businessFunction;
    const matchesSearch = searchQuery === '' ||
    service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    service.description.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesComplexity && matchesRoiTimeline && matchesBusinessFunction && matchesSearch;
  });

  const selectedServicesData = mockServices.filter((service) =>
  selectedForComparison.includes(service.id)
  );

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-32 bg-card rounded-2xl"></div>
            <div className="h-24 bg-card rounded-2xl"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) =>
              <div key={i} className="h-96 bg-card rounded-2xl"></div>
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <div className="relative overflow-hidden py-16 sm:py-20 lg:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
              <span className="text-sm font-semibold text-primary">8 AI Oplossingen</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
              Onze AI Services
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
              Ontdek onze complete portfolio van AI-oplossingen speciaal ontwikkeld voor Benelux MKB bedrijven. Van chatbots tot predictive analytics.
            </p>
          </div>

          <div className="glassmorphic rounded-2xl p-6 mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="relative w-full sm:w-96">
                <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Zoek services..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300 outline-none" />

              </div>

              {selectedForComparison.length > 0 &&
              <button
                onClick={() => setIsComparisonModalOpen(true)}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 font-semibold shadow-premium">

                  <Icon name="ScaleIcon" size={20} />
                  <span>Vergelijk ({selectedForComparison.length})</span>
                </button>
              }
            </div>
          </div>

          <FilterPanel
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters} />


          <div className="mb-6 flex items-center justify-between">
            <p className="text-muted-foreground">
              <span className="font-semibold text-foreground">{filteredServices.length}</span> services gevonden
            </p>
            {selectedForComparison.length > 0 &&
            <p className="text-sm text-muted-foreground">
                Selecteer maximaal 3 services om te vergelijken
              </p>
            }
          </div>

          {filteredServices.length === 0 ?
          <div className="glassmorphic rounded-2xl p-12 text-center">
              <Icon name="FaceFrownIcon" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-display font-bold text-foreground mb-2">
                Geen services gevonden
              </h3>
              <p className="text-muted-foreground mb-6">
                Probeer andere filters of wis de huidige selectie
              </p>
              <button
              onClick={handleClearFilters}
              className="px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 font-semibold">

                Wis alle filters
              </button>
            </div> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {filteredServices.map((service) =>
            <div key={service.id} className="relative">
                  <ServiceCard
                service={service}
                isActive={hoveredCard === service.id}
                onHover={setHoveredCard} />

                  <button
                onClick={() => toggleComparison(service.id)}
                disabled={!selectedForComparison.includes(service.id) && selectedForComparison.length >= 3}
                className={`absolute top-4 right-4 z-10 p-2 rounded-lg transition-all duration-300 ${
                selectedForComparison.includes(service.id) ?
                'bg-primary text-primary-foreground' :
                'bg-card/80 text-muted-foreground hover:bg-primary/10 hover:text-primary'} ${
                !selectedForComparison.includes(service.id) && selectedForComparison.length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}`}>

                    <Icon name={selectedForComparison.includes(service.id) ? 'CheckIcon' : 'PlusIcon'} size={20} />
                  </button>
                </div>
            )}
            </div>
          }

          <div className="glassmorphic rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-display font-bold text-foreground mb-4">
              Niet gevonden wat u zocht?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Wij ontwikkelen ook custom AI-oplossingen op maat voor uw specifieke bedrijfsbehoeften. Neem contact op voor een vrijblijvend gesprek.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-lg bg-cta text-cta-foreground hover:bg-cta/90 transition-all duration-300 font-semibold shadow-elevated hover:-translate-y-0.5">

                Neem contact op
              </Link>
              <Link
                href="/contact"
                className="px-8 py-4 rounded-lg border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-semibold">

                Plan een demo
              </Link>
            </div>
          </div>
        </div>
      </div>

      <ComparisonModal
        isOpen={isComparisonModalOpen}
        onClose={() => setIsComparisonModalOpen(false)}
        selectedServices={selectedServicesData}
        onRemoveService={removeFromComparison} />

    </div>);

};

export default ServicesOverviewInteractive;